import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { AmendmentDiffCopy, AmendmentModel, assertNever } from '@signalvote/shared';
import { useOpenAmendments } from '@/components/finance/shared';
import { filingDestinationPhrase, usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';

/**
 * Amendments the treasurer has started and not yet filed. Each one is a filing they still owe, so
 * it stays on the Close page whether or not a period is open: with none open it is the page's item,
 * with one open it sits beside it. How the state takes the amendment comes from the served
 * `AmendmentModel`. Self-hides when nothing is being amended.
 */
export function OpenAmendmentCard() {
  const amendments = useOpenAmendments();
  const portal = usePortalUploadModel();
  if (amendments.length === 0) return null;

  const nextStep = amendmentNextStep(portal.amendmentModel, filingDestinationPhrase(portal));

  return (
    <ul data-slot="open-amendments" className="flex flex-col gap-2">
      {amendments.map((a) => (
        <li
          key={a.id}
          data-testid={`open-amendment-${a.id}`}
          className="rounded-lg border border-slate-200 bg-white px-4 py-3"
        >
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                  Amending
                </span>
                <span className="text-[14px] font-semibold text-slate-900">
                  Amendment #{a.amendmentNumber} of {a.name}
                </span>
              </div>
              <p className="mt-1 text-[13px] text-slate-600">
                {AmendmentDiffCopy.correcting(a.name, a.amendmentNumber - 1)}
              </p>
              <p data-testid="open-amendment-next-step" className="mt-0.5 text-[13px] text-slate-600">
                {nextStep}
              </p>
            </div>
            <Link
              to="/admin/finance/reports/$reportId"
              params={{ reportId: a.id }}
              className="inline-flex shrink-0 items-center gap-1 text-[13px] font-medium text-slate-900 underline"
            >
              Review and file Amendment #{a.amendmentNumber}
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * What the treasurer does next with an amendment they have started, by the state's served
 * `AmendmentModel`. `destination` is `filingDestinationPhrase(portal)`, so the portal, email office
 * or neutral federal wording is resolved once, upstream. Null while the profile loads.
 */
function amendmentNextStep(model: AmendmentModel | null, destination: string): string {
  switch (model) {
    case AmendmentModel.PortalOnly:
      return `Key each change into ${destination} by hand. The portal follow-ups on this amendment list what to change. Then mark it filed here.`;
    case AmendmentModel.ElectronicResubmit:
      return `When it's ready, file it here. The amended report replaces the one on file in full; submit it to ${destination}, then mark it filed.`;
    case AmendmentModel.SelfContainedUpload:
      return `When it's ready, file it here and send the corrected report to ${destination}, then mark it filed.`;
    case null:
      return "When it's ready, file it here.";
    default:
      return assertNever(model);
  }
}
